import { Inject } from '@odinjs/odin';

import { Endpoint, generateReport, Report } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { ButtonAPI, DynamicFieldsAPI, FieldAPI, FormAPI, Metadata, PanelAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnChange, OnClick } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { format } from '@philips-emr/tws-core/lib/services/model-service';

import { ReportsAliases as Aliases, FragmentNames } from '../codegen';

import BaseController from './BaseController';

const DYNAMIC_REPORTS_GROUP = '13';

@Controller({ fragment: FragmentNames.REPORTS })
class ReportsController extends BaseController {

  @Inject(Aliases.FIELD_REPORT)
  private fieldReport: FieldAPI;

  @Inject(Aliases.BUTTON_GENERATE)
  private generateButton: ButtonAPI;

  @Inject(Aliases.DYNAMIC_FIELDS)
  private dynamicFields: DynamicFieldsAPI;

  @Inject(Aliases.FORM_DYNAMIC)
  private formDynamic: FormAPI;

  @Inject(Aliases.PANEL_DYNAMIC)
  private panelDynamic: PanelAPI;

  @OnClick(Aliases.BUTTON_GENERATE)
  onClickButtonGenerate(): void {
    if (this.panelDynamic.isVisible()) {
      this.formDynamic.submit();

      if (!this.formDynamic.isValid()) {
        return;
      }
    }

    const report = this.fieldReport.getValue() as Report;
    this.generateButton.setEnabled(false);
    const params = this.getParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    generateReport(Endpoint.POST.REPORTS, { nrSeqProvider }, DYNAMIC_REPORTS_GROUP, report, params)
      .finally(() => {
        this.changeCursor(false);
        this.formDynamic.setEditing(true);
        this.formDynamic.setLocked(false);
        this.generateButton.setEnabled(true);
      });
  }

  private changeCursor(isProgress: boolean): void {
    document.body.style.cursor = isProgress ? 'wait' : 'default';
  }

  @OnChange(Aliases.FIELD_REPORT)
  onChangeReport(e: ChangeEvent<Record<string, Value>>): void {

    if (!e.value) {
      this.formDynamic.setRecord({});
      this.panelDynamic.setVisible(false);
      this.generateButton.setEnabled(false);
      return;
    }

    this.generateButton.setEnabled(!!e.value.nrSequencia && e.value.nrSequencia !== '');
    const nrSeqReport = e.value.nrSequencia as string;

    HttpService.get(Endpoint.GET.DYNAMICS_PARAMETERS, { nrSeqReport })
      .then(resp => {
        const metadata = resp.body as Metadata;
        if (metadata) {
          this.panelDynamic.setVisible(true);
          this.formDynamic.setRecord({});
          this.dynamicFields.setMetadata(resp.body as Metadata);
        } else {
          this.panelDynamic.setVisible(false);
        }
      })
      .catch(() => {
        this.panelDynamic.setVisible(false);
      });
  }

  private getParams(): {} {
    let params = {};

    if (this.panelDynamic.isVisible()) {
      const record = this.formDynamic.getRecord();
      Object.keys(record).forEach(k => {
        if (record[k]) {
          if (typeof record[k] === 'object') {
            const value = record[k] as Record<string, Value>;
            params = { ...params, [k]: value.value, DYNAMIC: true };
          } else {
            const value = format('date', record[k]);
            params = { ...params, [k]: value, DYNAMIC: true };
          }
        }
      });
    }

    return params;
  }
}

export default ReportsController;
